import React, { useEffect, useState, useRef } from "react";
import DataTable from "../../Pages/DataTable";
import { funcAreaAPI } from "../../Pages/api";

export default function FuncAreaViewer({
  autoLoad = true,
  selectable = false,
  onSelect = () => {},
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const closeTimer = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await funcAreaAPI.getAll();
      const active = Array.isArray(data) ? data.filter(r => r.active_status === '1' || r.active_status === 1 || r.active_status === 'Active') : [];
      setRows(active);
      setCurrentPage(1);
      setSelectedIdx(null);
    } catch (e) {
      console.error("FuncAreaViewer load error:", e);
      setRows([]);
      setError("Failed to load functional areas. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  useEffect(() => {
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const pagedRows = rows.slice(startIdx, startIdx + pageSize);

  const handleRadioSelect = (pageRelativeIdx) => {
    const absoluteIdx = startIdx + pageRelativeIdx;
    setSelectedIdx(absoluteIdx);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      onSelect(rows[absoluteIdx]);
    }, 500);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const columns = [
    ...(selectable
      ? [
          {
            header: "",
            className: "dt-col-narrow",
            renderCell: (_val, _row, pageRelativeIdx) => (
              <input
                type="radio"
                name="funcAreaSelect"
                checked={selectedIdx === startIdx + pageRelativeIdx}
                onChange={() => handleRadioSelect(pageRelativeIdx)}
              />
            ),
          },
        ]
      : []),
    { header: "Area Code", accessor: (row) => row.func_sub_area_code ?? "", className: "dt-col-sm" },
    { header: "Area Name", accessor: (row) => row.func_sub_area_name ?? "", className: "dt-col-lg" },
    { header: "Description", accessor: (row) => row.func_area_desc ?? "", className: "dt-col-lg" },
  ];

  const paginationStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    margin: "10px 0 0",
    fontFamily: "GEG-Body, sans-serif",
    fontSize: 12,
  };

  const pgBtnStyle = {
    padding: "3px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    background: "#f7f7f7",
    cursor: "pointer",
    fontFamily: "GEG-Body, sans-serif",
    fontSize: 12,
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          type="button"
          onClick={load}
          style={{ ...pgBtnStyle, background: "#810055", color: "#fff", border: "none", borderRadius: 30, padding: "4px 14px" }}
        >
          Refresh
        </button>
      </div>

      {loading && (
        <p style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, color: "#666", marginTop: 10 }}>
          Loading functional areas…
        </p>
      )}

      {error && (
        <p style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, color: "red", marginTop: 10 }}>
          {error}
        </p>
      )}

      {!loading && (
        <>
          <DataTable
            columns={columns}
            data={pagedRows}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No functional areas found"
            rowKey={(row, i) => `${row.func_sub_area_code}-${startIdx + i}`}
          />

          {rows.length > 0 && (
            <div style={paginationStyle}>
              <span>Rows per page:</span>
              <select value={pageSize} onChange={handlePageSizeChange} style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12 }}>
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>

              <button type="button" style={pgBtnStyle} disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>&laquo;</button>
              <button type="button" style={pgBtnStyle} disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>&lsaquo; Prev</button>

              <span>Page {currentPage} of {totalPages}</span>

              <button type="button" style={pgBtnStyle} disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next &rsaquo;</button>
              <button type="button" style={pgBtnStyle} disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>&raquo;</button>

              <span style={{ marginLeft: 8, color: "#666" }}>({rows.length} total)</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
